import React from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "../../../shared";
import { JobWorkspaceIcon } from "./icons";
import { DetailField, StatusPill, WorkspaceButton, WorkspaceCard } from "./ui";

/**
 * Job identity, actions, and the four-column summary.
 * @param {{
 *   view: object,
 *   jobsPath?: string,
 *   canEdit?: boolean,
 *   onEdit?: () => void,
 *   onExport?: () => void,
 *   moreActions?: Array<{ title: string, onClick?: () => void }>,
 * }} props
 * @returns {JSX.Element}
 */
export function JobOverview({
  view,
  jobsPath = "/jobs",
  canEdit = false,
  onEdit,
  onExport,
  moreActions = [],
}) {
  return (
    <div className="flex flex-col gap-[22px]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-[11px]">
          <Link
            to={jobsPath}
            className="inline-flex items-center gap-[6px] text-[11px] font-normal leading-normal text-[#6e7b84] hover:text-[#202b33]"
          >
            <JobWorkspaceIcon name="arrowLeft" />
            All jobs
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[28px] font-normal leading-normal text-[#202b33]">
              {view.jobNumber}
            </h1>
            <StatusPill
              label={view.statusLabel}
              tone={
                ["completed", "paid", "invoiced"].includes(view.statusId)
                  ? "approved"
                  : ["in_progress", "dispatched", "scheduled"].includes(view.statusId)
                    ? "progress"
                    : "neutral"
              }
              dot
            />
            {view.priorityLabel ? (
              <StatusPill
                label={view.priorityLabel}
                tone={view.priorityTone === "high" ? "priority" : "neutral"}
              />
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-[9px] text-[12px] font-normal leading-normal">
            <span className="text-[#6e7b84]">{view.loadLabel}</span>
            <span className="text-[#909ba3]">·</span>
            <span className="text-[#6e7b84]">{view.companyLabel}</span>
            <span className="text-[#909ba3]">·</span>
            <span className="inline-flex items-center gap-[5px] text-[#6e7b84]">
              <span
                className={`size-[5px] rounded-full ${
                  view.active ? "bg-[#167366]" : "bg-[#909ba3]"
                }`}
              />
              {view.active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
        <div className="job-workspace-actions flex items-start gap-2">
          <WorkspaceButton
            onClick={onExport}
            icon={<JobWorkspaceIcon name="download" />}
          >
            Export
          </WorkspaceButton>
          <WorkspaceButton
            tone="primary"
            disabled={!canEdit}
            onClick={onEdit}
            icon={<JobWorkspaceIcon name="pencil" />}
          >
            Edit job
          </WorkspaceButton>
          <Dropdown
            closeOnClick
            align="right"
            data={moreActions}
            trigger={
              <button
                type="button"
                aria-label="More actions"
                className="inline-flex size-9 items-center justify-center rounded-[6px] border border-[#e2e7ea] bg-white"
              >
                <JobWorkspaceIcon name="ellipsis" />
              </button>
            }
          />
        </div>
      </div>
      <WorkspaceCard className="flex flex-wrap items-start gap-6 p-5">
        {view.summary.map((item, index) => (
          <React.Fragment key={item.label}>
            {index > 0 ? <span className="h-11 w-px shrink-0 bg-[#e2e7ea]" /> : null}
            <DetailField label={item.label} value={item.value} detail={item.detail} />
          </React.Fragment>
        ))}
      </WorkspaceCard>
    </div>
  );
}
